/**
 * SocialDeepDive Plugin - TypeScript Types
 *
 * Type definitions for social media platform breakdown functionality.
 * Enables detailed tracking of followers, impressions, and engagements
 * across 12 different social media platforms.
 */

import { ComponentType } from 'react';

/**
 * Platform metrics for a single social media platform
 * Stores the raw data entered by the user for one platform
 */
export interface PlatformMetrics {
  platform: string; // 'instagram', 'facebook', 'youtube', etc.

  // Followers metrics
  followers: number;
  followers_cpm: number;

  // Impressions metrics
  impressions: number;
  impressions_cpm: number;

  // Engagements metrics
  engagements: number;
  engagements_cpm: number;
}

/**
 * Aggregated totals across all platforms
 * Includes weighted average CPM calculations
 */
export interface TotalMetrics {
  // Volume totals (sum of all platforms)
  followers: number;
  impressions: number;
  engagements: number;

  // Weighted average CPMs
  followers_cpm: number;
  impressions_cpm: number;
  engagements_cpm: number; // This maps to valuations.social_cpm

  // Active platform counts (how many platforms have data)
  activePlatforms: {
    followers: number;
    impressions: number;
    engagements: number;
  };
}

/**
 * Platform definition (metadata about a social media platform)
 * Used for rendering UI and displaying platform-specific information
 */
export interface SocialPlatform {
  id: string; // 'instagram', 'facebook', etc.
  name: string; // 'Instagram', 'Facebook', etc.
  icon: ComponentType<{ className?: string; size?: number }>; // React icon component
  color: string; // Brand color (hex)

  // Platform-specific terminology
  followersTerm: string; // 'Followers', 'Subscribers', 'Members'
  impressionsTerm: string; // 'Impressions', 'Views', 'Page Views'
  engagementsTerm: string; // 'Engagements', 'Upvotes', 'Reblogs'
}

/**
 * Complete dataset for SocialDeepDive
 * Includes both platform data and calculated totals
 */
export interface SocialDeepDiveData {
  platforms: PlatformMetrics[];
  totals: TotalMetrics;
}

/**
 * Organization settings for enabled platforms
 * Stores which platforms an organization has activated
 */
export interface OrganizationSocialSettings {
  id: string;
  organization_id: string;
  enabled_platforms: string[]; // ['instagram', 'facebook', 'tiktok']
  created_at: string;
  updated_at: string;
}

/**
 * Database row from valuation_social_platforms table
 * Includes all metadata fields
 */
export interface ValuationSocialPlatformRow extends PlatformMetrics {
  id: string;
  valuation_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Input type for creating/updating platform metrics
 * Omits auto-generated fields
 */
export interface PlatformMetricsInput extends PlatformMetrics {
  valuation_id?: string; // Optional for client-side forms
  organization_id?: string; // Optional for client-side forms
}

/**
 * API request body for saving platform data
 */
export interface SavePlatformDataRequest {
  valuationId: string;
  organizationId: string;
  platforms: PlatformMetrics[];
}

/**
 * API response for fetching platform data
 */
export interface FetchPlatformDataResponse {
  platforms: PlatformMetrics[];
  totals: TotalMetrics;
  enabledPlatforms: string[];
}

/**
 * API request body for updating organization settings
 */
export interface UpdateSettingsRequest {
  enabled_platforms: string[];
}

/**
 * API response for fetching organization settings
 */
export interface FetchSettingsResponse {
  enabled_platforms: string[];
}

/**
 * Type guard to check if a platform ID is valid
 */
export function isValidPlatform(platform: string): boolean {
  const validPlatforms = [
    'instagram',
    'facebook',
    'youtube',
    'tiktok',
    'linkedin',
    'reddit',
    'tumblr',
    'slack',
    'vimeo',
    'behance',
    'yelp',
    'flickr',
  ];
  return validPlatforms.includes(platform);
}

/**
 * Default empty platform metrics
 */
export const DEFAULT_PLATFORM_METRICS: Omit<PlatformMetrics, 'platform'> = {
  followers: 0,
  followers_cpm: 0,
  impressions: 0,
  impressions_cpm: 0,
  engagements: 0,
  engagements_cpm: 0,
};

/**
 * Default empty totals
 */
export const DEFAULT_TOTALS: TotalMetrics = {
  followers: 0,
  impressions: 0,
  engagements: 0,
  followers_cpm: 0,
  impressions_cpm: 0,
  engagements_cpm: 0,
  activePlatforms: {
    followers: 0,
    impressions: 0,
    engagements: 0,
  },
};
